import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import CommentForm from './CommentForm.jsx';

/**
 * Popover component that appears when text is selected
 */
const CommentPopover = ({
  selection,
  selectionPosition,
  onSubmit,
  onCancel,
  isVisible = false,
  className = ''
}) => {
  const [showForm, setShowForm] = useState(false);
  const [savedSelection, setSavedSelection] = useState(null);
  const [savedPosition, setSavedPosition] = useState(null);
  const popoverRef = useRef(null);

  // Reset form visibility when selection changes (but only if form is not showing)
  useEffect(() => {
    if (!showForm) {
      setShowForm(false);
      setSavedSelection(null);
      setSavedPosition(null);
    }
  }, [selection, showForm]);

  /**
   * Handle comment button click
   */
  const handleCommentClick = () => {
    console.log('Comment button clicked, showing form');
    console.log('Current selection:', selection);
    console.log('Current isVisible:', isVisible);
    // Save the selection before showing the form (so we don't lose it when textarea focuses)
    setSavedSelection(selection);
    setSavedPosition(selectionPosition);
    setShowForm(true);
  };

  /**
   * Handle form submission
   */
  const handleFormSubmit = async (content) => {
    // Use saved selection if current selection is lost
    const activeSelection = savedSelection || selection;
    if (!activeSelection) return;

    const commentData = {
      blockIndex: activeSelection.blockIndex,
      startOffset: activeSelection.startOffset,
      endOffset: activeSelection.endOffset,
      highlightedText: activeSelection.text,
      content
    };

    await onSubmit(commentData);
    setShowForm(false);
    setSavedSelection(null);
    setSavedPosition(null);
    onCancel && onCancel();
  };

  /**
   * Handle form cancel
   */
  const handleFormCancel = () => {
    setShowForm(false);
    setSavedSelection(null);
    setSavedPosition(null);
    onCancel && onCancel();
  };

  // Use saved selection/position if form is showing and original was lost
  const activeSelection = showForm && savedSelection ? savedSelection : selection;
  const activePosition = showForm && savedPosition ? savedPosition : selectionPosition;

  // Don't render if not visible AND form is not shown, OR if we don't have selection data
  if ((!isVisible && !showForm) || !activeSelection || !activePosition) {
    console.log('Popover hidden - isVisible:', isVisible, 'showForm:', showForm, 'hasSelection:', !!activeSelection);
    return null;
  }
  
  console.log('Popover rendering - isVisible:', isVisible, 'showForm:', showForm);

  const { top, left, width } = activePosition;

  return (
    <div
      ref={popoverRef}
      className={`comment-popover ${className}`}
      onMouseEnter={() => {
        // Keep the popover visible when hovering over it
        if (isVisible) {
          // Prevent it from disappearing
        }
      }}
      style={{
        position: 'absolute',
        top: `${top - 40}px`, // Position above the selection
        left: `${left + width / 2}px`, // Center horizontally
        transform: 'translateX(-50%)',
        zIndex: 1000,
        opacity: (isVisible || showForm) ? 1 : 0,
        transition: 'opacity 0.15s ease, transform 0.15s ease',
        pointerEvents: (isVisible || showForm) ? 'auto' : 'none'
      }}
    >
      {showForm ? (
        <CommentForm
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          placeholder={`Comment on "${activeSelection.text.substring(0, 50)}${activeSelection.text.length > 50 ? '...' : ''}"`}
          submitLabel="Comment"
          cancelLabel="Cancel"
          className="selection-comment-form"
        />
      ) : (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCommentClick();
          }}
          onMouseDown={(e) => {
            // Prevent mousedown from clearing the selection
            e.preventDefault();
          }}
          className="notion-comment-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#2383e2',
            color: '#ffffff',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1a73d1';
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2383e2';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span>Comment</span>
        </button>
      )}
    </div>
  );
};

CommentPopover.propTypes = {
  selection: PropTypes.shape({
    text: PropTypes.string.isRequired,
    blockIndex: PropTypes.number.isRequired,
    startOffset: PropTypes.number.isRequired,
    endOffset: PropTypes.number.isRequired
  }),
  selectionPosition: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  isVisible: PropTypes.bool,
  className: PropTypes.string
};

export default CommentPopover;
